import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Star, Truck } from "lucide-react";
import api from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { ShopBySchool } from "../components/ShopBySchool";

const HERO_IMG = "https://images.unsplash.com/photo-1760565030346-4b947220fe3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxjb2xvcmZ1bCUyMGxpZmVzdHlsZSUyMHNob3BwaW5nJTIwYmFncyUyMGZhc2hpb258ZW58MHx8fHwxNzgzMzQ3MzU0fDA&ixlib=rb-4.1.0&q=85";

const MARQUEE = ["FREE SHIPPING $50+", "★", "30-DAY RETURNS", "★", "NEW DROPS WEEKLY", "★", "UP TO 60% OFF", "★"];

const CATEGORIES = [
  { name: "Electronics", color: "bg-brand", emoji: "⚡" },
  { name: "Home", color: "bg-brand-pink", emoji: "🏠" },
  { name: "Fashion", color: "bg-brand-yellow", emoji: "👕" },
  { name: "Kitchen", color: "bg-brand-green", emoji: "🍳" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 lg:pt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="lg:col-span-7 flex flex-col justify-center">
            <span className="inline-flex items-center gap-2 self-start rounded-full px-4 py-1.5 text-xs font-black uppercase tracking-wider border-2 border-ink bg-brand-yellow shadow-hard-sm mb-6">
              <Zap className="w-4 h-4" /> New arrivals just dropped
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-black leading-[0.95] mb-6">
              Snag the <span className="text-brand">good stuff</span> before it's gone.
            </h1>
            <p className="text-lg text-muted-foreground max-w-md mb-8">
              Curated finds, unreal prices, delivered to your door. Shop thousands of trending products all in one fun place.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold bg-brand text-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all" data-testid="hero-shop-button">
                Start Shopping <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/shop?category=Electronics" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold bg-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all" data-testid="hero-electronics-button">
                Shop Tech
              </Link>
            </div>
            <div className="flex items-center gap-6 mt-10">
              <div className="flex items-center gap-2"><Star className="w-5 h-5 fill-brand-yellow text-ink" /><span className="font-bold text-sm">4.9/5 rating</span></div>
              <div className="flex items-center gap-2"><Truck className="w-5 h-5 text-brand" /><span className="font-bold text-sm">50k+ orders shipped</span></div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-5 relative">
            <div className="rounded-3xl border-2 border-ink shadow-hard-lg overflow-hidden aspect-[4/5] bg-brand-pink">
              <img src={HERO_IMG} alt="Snagly colorful shopping" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-brand-green rounded-2xl border-2 border-ink shadow-hard px-5 py-3 rotate-[-4deg]">
              <p className="font-black text-lg">Up to 60% OFF 🎉</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <section className="mt-16 border-y-2 border-ink bg-brand overflow-hidden py-4">
        <div className="marquee-track animate-marquee">
          {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((t, i) => (
            <span key={i} className="text-white font-black text-lg uppercase tracking-wider px-6">{t}</span>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-8">Shop by category</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {CATEGORIES.map((c) => (
            <Link key={c.name} to={`/shop?category=${c.name}`} data-testid={`category-${c.name}`}
              className={`${c.color} rounded-3xl border-2 border-ink shadow-hard p-6 aspect-[4/3] flex flex-col justify-between hover:shadow-hard-lg hover:-translate-y-1 transition-all`}>
              <span className="text-4xl">{c.emoji}</span>
              <div className="flex items-center justify-between">
                <span className="font-black text-lg font-heading">{c.name}</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Shop by School */}
      <ShopBySchool />

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Trending now 🔥</h2>
          <Link to="/shop" className="font-bold text-brand flex items-center gap-1 hover:gap-2 transition-all" data-testid="view-all-link">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>
    </div>
  );
}
